import { useFormik } from 'formik';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { Button } from '~/ui';
import { getCompaniesModuleOptions } from '~/utils/getCompaniesModuleOptions';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import styles from './PointsFilters.module.scss';

export interface PointsFilterValues {
  dates?: string[];
  companies?: { names: string[] };
  accommodation_ids?: number[];
  promo_loyalty_ids?: number[];
}

interface Props {
  onApply: (filters: PointsFilterValues) => void;
  isLoading?: boolean;
}

interface FormValues {
  dates: { start_date: Dayjs | null; end_date: Dayjs | null };
  organizations: { id: number; name: string; full_name: string }[];
  accommodations: { id: number; name: string; full_name: string }[];
  promotions: { id: number; name: string; full_name: string }[];
}

export const PointsFilters = ({ onApply, isLoading }: Props) => {
  const { t } = useTranslation();
  const toastError = useToastError();

  const { data: companies } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/user-company');
      return data;
    },
    queryKey: ['user-company'],
    onError: toastError,
  });

  const { data: accommodations, isLoading: isAccommodationsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/accommodations', {
        params: { sort: '', per_page: 100, field: '', page: 1 },
      });
      return data;
    },
    queryKey: ['accommodations'],
    onError: toastError,
  });

  const { data: promoLoyalty, isLoading: isPromoLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/promo-loyalty', {
        params: { page: 1, perPage: 100 },
      });
      return data;
    },
    queryKey: ['promo-loyalty-list'],
    onError: toastError,
  });

  const companyOptions = getCompaniesModuleOptions(companies?.companies);

  const formik = useFormik<FormValues>({
    initialValues: {
      dates: { start_date: null, end_date: null },
      organizations: [],
      accommodations: [],
      promotions: [],
    },
    onSubmit: values => {
      const filters: PointsFilterValues = {
        ...(values.dates.start_date && values.dates.end_date
          ? {
              dates: [
                dayjs(values.dates.start_date).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                dayjs(values.dates.end_date).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
              ],
            }
          : {}),
        ...(values.organizations.length
          ? { companies: { names: values.organizations.map(o => o.name) } }
          : {}),
        ...(values.accommodations.length
          ? { accommodation_ids: values.accommodations.map(a => a.id) }
          : {}),
        ...(values.promotions.length
          ? { promo_loyalty_ids: values.promotions.map(p => p.id) }
          : {}),
      };
      onApply(filters);
    },
  });

  const isAnyFilled =
    !!formik.values.dates.start_date ||
    !!formik.values.dates.end_date ||
    formik.values.organizations.length > 0 ||
    formik.values.accommodations.length > 0 ||
    formik.values.promotions.length > 0;

  const handleReset = () => {
    formik.resetForm();
    onApply({});
  };

  return (
    <form onSubmit={formik.handleSubmit} className={styles.container}>
      <div className={styles.fields}>
        <div className={styles.dateField}>
          <CustomDatePicker
            title={t('accrual_write_off_period')}
            value={[formik.values.dates.start_date, formik.values.dates.end_date]}
            placeholder={[t('date'), t('date')]}
            showPresets={true}
            disabledDate={current => current && current.isAfter(dayjs(), 'day')}
            onChange={(name, value) => {
              formik.setFieldValue(
                name === 'start_date' ? 'dates.start_date' : 'dates.end_date',
                value
              );
            }}
          />
        </div>
        <div className={styles.field}>
          <CustomSelect
            label={t('company')}
            options={companyOptions}
            value={formik.values.organizations}
            onChange={value => formik.setFieldValue('organizations', value)}
            placeholder={t('validate_organizations')}
            multiselect
          />
        </div>
        <div className={styles.field}>
          <CustomSelect
            label={t('accommodations')}
            options={accommodations?.data}
            value={formik.values.accommodations}
            onChange={value => formik.setFieldValue('accommodations', value)}
            placeholder={t('select_accommodation_facility')}
            disabled={isAccommodationsLoading}
            multiselect
          />
        </div>
        <div className={styles.field}>
          <CustomSelect
            label={t('promotion')}
            options={promoLoyalty?.data}
            value={formik.values.promotions}
            onChange={value => formik.setFieldValue('promotions', value)}
            placeholder={t('placeholder_promotion')}
            disabled={isPromoLoading}
            multiselect
          />
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          text={t('apply_filter')}
          type="submit"
          className={styles.applyBtn}
          disabled={!isAnyFilled || isLoading}
          isLoading={isLoading}
        />
        {isAnyFilled && (
          <Button
            text={t('discard')}
            type="button"
            className={styles.resetBtn}
            onClick={handleReset}
          />
        )}
      </div>
    </form>
  );
};
